import { z } from "zod";
import type { UiSchema } from "./task";

export const toolDisplayMetadataSchema = z.object({
  widget: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
});

export type ToolDisplayMetadata = z.infer<typeof toolDisplayMetadataSchema>;

export const toolDisplayEnvelopeSchema = z.object({
  display: toolDisplayMetadataSchema.optional(),
  data: z.record(z.string(), z.unknown()),
  ui: z
    .record(
      z.string(),
      z
        .object({
          "ui:widget": z.string().optional(),
          "ui:title": z.string().optional(),
          "ui:description": z.string().optional(),
          "ui:options": z.record(z.string(), z.unknown()).optional(),
        })
        .passthrough()
    )
    .optional(),
});

export type ToolDisplayEnvelope<
  TData extends Record<string, unknown> = Record<string, unknown>,
> = {
  display?: ToolDisplayMetadata;
  data: TData;
  ui?: Record<string, UiSchema>;
};

/** Detect agent-sent display envelopes without matching arbitrary tool payloads. */
export function isToolDisplayEnvelope(
  value: unknown
): value is ToolDisplayEnvelope {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;
  if (!("data" in record)) {
    return false;
  }

  const data = record.data;
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return false;
  }

  return "display" in record || "ui" in record;
}
